// Section: Fee Rules (gated by partsFeeRules)
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import type { FeeKind, FeeCalcType } from '@/lib/parts-financial/calculator';

export interface FeeRuleDraft {
  id?: string;
  kind: FeeKind;
  label: string;
  calc_type: FeeCalcType;
  amount: number;
  cap_amount?: number | null;
  taxable: boolean;
  enabled: boolean;
}

const KIND_OPTIONS: { value: FeeKind; label: string }[] = [
  { value: 'shop_supplies', label: 'Shop Supplies' },
  { value: 'tire_disposal', label: 'Tire Disposal' },
  { value: 'hazmat', label: 'Hazmat' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'fluid_disposal', label: 'Fluid Disposal' },
  { value: 'core_charge', label: 'Core Charge' },
  { value: 'custom', label: 'Custom' },
];

const CALC_OPTIONS: { value: FeeCalcType; label: string }[] = [
  { value: 'percent_labor', label: '% of Labor' },
  { value: 'percent_ticket', label: '% of Ticket' },
  { value: 'flat', label: 'Flat $' },
  { value: 'per_unit', label: 'Per Unit $' },
];

interface Props {
  value: FeeRuleDraft[];
  onChange: (next: FeeRuleDraft[]) => void;
}

export function FeeRulesSection({ value, onChange }: Props) {
  const enabled = useFeatureFlag('partsFeeRules');
  if (!enabled) return null;

  const update = (idx: number, patch: Partial<FeeRuleDraft>) => {
    onChange(value.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const addNew = () => onChange([
    ...value,
    { kind: 'custom', label: 'New fee', calc_type: 'flat', amount: 0, cap_amount: null, taxable: true, enabled: true },
  ]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fee Rules</CardTitle>
        <CardDescription>Templates applied to estimates and quotes. Percentages use decimals (0.08 = 8%).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {value.length === 0 && (
          <p className="text-sm text-muted-foreground">No fee templates yet. Add your first below.</p>
        )}

        {value.map((rule, idx) => (
          <div key={idx} className="rounded-md border p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3 space-y-1">
                <Label className="text-xs">Kind</Label>
                <Select value={rule.kind} onValueChange={(v) => update(idx, { kind: v as FeeKind })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 space-y-1">
                <Label className="text-xs">Label</Label>
                <Input value={rule.label} onChange={(e) => update(idx, { label: e.target.value })} />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Calc</Label>
                <Select value={rule.calc_type} onValueChange={(v) => update(idx, { calc_type: v as FeeCalcType })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CALC_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1 space-y-1">
                <Label className="text-xs">Amount</Label>
                <Input type="number" step="0.0001" value={rule.amount}
                  onChange={(e) => update(idx, { amount: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="md:col-span-1 space-y-1">
                <Label className="text-xs">Cap $</Label>
                <Input type="number" step="0.01" value={rule.cap_amount ?? ''}
                  placeholder="—"
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseFloat(e.target.value);
                    update(idx, { cap_amount: v == null || isNaN(v as number) ? null : (v as number) });
                  }} />
              </div>
              <div className="md:col-span-1 flex items-center justify-center gap-1 pb-2">
                <Switch checked={rule.taxable} onCheckedChange={(c) => update(idx, { taxable: c })} />
                <Label className="text-xs">Tax</Label>
              </div>
              <div className="md:col-span-1 flex items-center justify-end pb-2">
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
            {!rule.enabled && (
              <p className="text-xs text-muted-foreground italic">Disabled — will not apply to estimates.</p>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={rule.enabled} onCheckedChange={(c) => update(idx, { enabled: c })} />
              <Label className="text-xs">Enabled</Label>
            </div>
          </div>
        ))}

        <Button type="button" variant="outline" onClick={addNew}>
          <Plus className="h-4 w-4 mr-2" /> Add Fee Template
        </Button>
      </CardContent>
    </Card>
  );
}
